import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { X, Copy, Send, Check, Loader2 } from "lucide-react"
import { useGetTourShareTemplatesQuery } from "../../../../../app/services/book/tour_share_templates"
import { useLazyGetSavedTourShareTextQuery } from "../../../../../app/services/book/saved_tours"

const ShareTourModal = ({ tourId, open, onClose }) => {
  const { t } = useTranslation()
  const [templateId, setTemplateId] = useState(null)
  const [copied, setCopied] = useState(false)

  const { data: templatesData } = useGetTourShareTemplatesQuery(undefined, { skip: !open })
  const templates = templatesData?.templates || []

  const [fetchShareText, { data: shareData, isFetching }] = useLazyGetSavedTourShareTextQuery()

  useEffect(() => {
    if (!open || !tourId) return
    fetchShareText({ id: tourId, template_id: templateId || undefined })
  }, [open, tourId, templateId, fetchShareText])

  useEffect(() => {
    if (!open) return
    if (templateId == null && templates.length) {
      const def = templates.find((tpl) => tpl.is_default) || templates[0]
      setTemplateId(def.id)
    }
  }, [open, templates, templateId])

  useEffect(() => {
    if (!open) setTemplateId(null)
  }, [open])

  const text = shareData?.text || ""

  const handleCopy = async () => {
    if (!text) return
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const handleTelegramShare = () => {
    if (!text) return
    const url = `https://t.me/share/url?url=&text=${encodeURIComponent(text)}`
    window.open(url, "_blank", "noopener,noreferrer")
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E7EAEF] shadow-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#1F2937]">{t("saved_tours.share.title")}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#9AA5B4] hover:bg-[#F4F6F9] transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-3">
          <select
            value={templateId || ""}
            onChange={(e) => setTemplateId(Number(e.target.value))}
            className="w-full text-sm border border-[#E7EAEF] rounded-xl px-3 py-2
                       text-[#1F2937] bg-white focus:outline-none focus:ring-2 focus:ring-[#1F2937]/10"
          >
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
                {tpl.is_default ? ` (${t("saved_tours.share.default")})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="relative">
          <textarea
            readOnly
            value={isFetching ? "" : text}
            rows={6}
            className="w-full text-sm border border-[#E7EAEF] rounded-xl p-3 bg-[#F9FAFB]
                       text-[#1F2937] resize-none focus:outline-none"
          />
          {isFetching && (
            <div className="absolute inset-0 flex items-center justify-center text-[#9AA5B4]">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleCopy}
            disabled={!text || isFetching}
            className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl
                       border border-[#E7EAEF] text-sm font-medium text-[#1F2937]
                       hover:bg-[#F4F6F9] transition-colors disabled:opacity-50"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? t("saved_tours.share.copied") : t("saved_tours.share.copy")}
          </button>
          {/* <button
            onClick={handleTelegramShare}
            disabled={!text || isFetching}
            className="flex-1 flex items-center justify-center gap-2 px-3.5 py-2.5 rounded-xl
                       bg-[#1F2937] text-white text-sm font-medium
                       hover:bg-[#111827] transition-colors disabled:opacity-50"
          >
            <Send size={16} />
            {t("saved_tours.share.send_telegram")}
          </button> */}
        </div>
      </div>
    </div>
  )
}

export default ShareTourModal
